import type { AxiosInstance } from 'axios';

import { $$api } from '@doger/core/shared/api';

import type {
  ErrorRateMetricsResponse,
  FindManyUserChatsResponse,
  FindManyUserNotes,
  FindManyUsersResponse,
  FindOneUserChatResponse,
  FindOneUserResponse,
  MetricsResponse,
  RequestsPerSecondResponse,
  ResponseTimeMetricsResponse,
  SessionsResponseDto,
  SystemMetricsResponse,
  UserRole,
} from './types';

class PrivateApi {
  public constructor(private readonly httpClient: AxiosInstance) {}

  public async findManyUsers(args: { take: number; skip: number }) {
    return this.httpClient
      .request<FindManyUsersResponse>({
        method: 'GET',
        url: '/users',
        params: {
          take: args.take,
          skip: args.skip,
        },
      })
      .then((res) => res.data);
  }

  public async findOneUser(args: { id: string }) {
    return this.httpClient
      .request<FindOneUserResponse>({
        method: 'GET',
        url: `/users/${args.id}`,
      })
      .then((res) => res.data);
  }

  public async findUserChats(args: {
    userId: string;
    take: number;
    skip: number;
  }) {
    return this.httpClient
      .request<FindManyUserChatsResponse>({
        method: 'GET',
        url: `/ai/admin/user-chats`,
        params: {
          userId: args.userId,
          take: args.take,
          skip: args.skip,
        },
      })
      .then((res) => res.data)
      .then((data) => {
        if (Array.isArray(data)) {
          return data.map((chatWithSession) => ({
            ...chatWithSession.meta,
            session: chatWithSession,
          }));
        }

        return data.chats.map((chat) => ({
          ...chat,
          session:
            data.sessions.find((session) => session.id === chat.sessionId) ??
            null,
        }));
      });
  }

  public async findUserChat(args: { userId: string; sessionId: string }) {
    return this.httpClient
      .request<FindOneUserChatResponse>({
        method: 'GET',
        url: `/ai/admin/sessions/${args.sessionId}`,
        params: {
          userId: args.userId,
        },
      })
      .then((res) => res.data);
  }

  public async toggleUserWhitelist(args: {
    userId: string;
    isWhitelisted: boolean;
  }) {
    return this.httpClient
      .request<{ success: boolean }>({
        method: 'PATCH',
        url: `/auth/${args.userId}/whitelist`,
        data: {
          isWhitelisted: args.isWhitelisted,
        },
      })
      .then((res) => res.data);
  }

  public async toggleUserRole(args: { userId: string; role: UserRole }) {
    return this.httpClient
      .request<{ success: boolean }>({
        method: 'PATCH',
        url: `/auth/${args.userId}/role`,
        data: {
          role: args.role,
        },
      })
      .then((res) => res.data);
  }

  public async deleteUser(args: { userId: string }) {
    return this.httpClient
      .request<{ success: boolean }>({
        method: 'DELETE',
        url: `/users`,
        data: {
          id: args.userId,
        },
      })
      .then((res) => res.data);
  }

  public async findManyNote(args: {
    userId: string;
    take: number;
    skip: number;
  }) {
    return this.httpClient
      .request<FindManyUserNotes>({
        method: 'GET',
        url: `/users/${args.userId}/notes`,
        params: {
          take: args.take,
          skip: args.skip,
        },
      })
      .then((res) => res.data);
  }

  public async createNote(args: { userId: string; content: string }) {
    return this.httpClient
      .request<FindManyUserNotes>({
        method: 'POST',
        url: `/users/${args.userId}/notes`,
        data: {
          content: args.content,
        },
      })
      .then((res) => res.data);
  }

  public async getAdminDashboard() {
    return this.httpClient
      .request<MetricsResponse>({
        method: 'GET',
        url: '/metrics',
      })
      .then((res) => res.data);
  }

  public async getSystemMetrics() {
    return this.httpClient
      .request<SystemMetricsResponse>({
        method: 'GET',
        url: '/metrics/s',
      })
      .then((res) => res.data);
  }

  public async getResponseTimeMetrics() {
    return this.httpClient
      .request<ResponseTimeMetricsResponse>({
        method: 'GET',
        url: '/metrics/rtm',
      })
      .then((res) => res.data);
  }

  public async getErrorRateMetrics() {
    return this.httpClient
      .request<ErrorRateMetricsResponse>({
        method: 'GET',
        url: '/metrics/erm',
      })
      .then((res) => res.data);
  }

  public async getRequestsPerSecond() {
    return this.httpClient
      .request<RequestsPerSecondResponse>({
        method: 'GET',
        url: '/metrics/rps',
      })
      .then((res) => res.data);
  }

  public async getUserSessions(args: {
    userId: string;
    take: number;
    skip: number;
  }) {
    return this.httpClient
      .request<SessionsResponseDto>({
        method: 'GET',
        url: `/auth/sessions/${args.userId}`,
        params: {
          take: args.take,
          skip: args.skip,
        },
      })
      .then((res) => res.data);
  }

  public async endAllUserSessions(userId: string) {
    return this.httpClient
      .request<{ success: boolean }>({
        method: 'POST',
        url: `/auth/sessions/${userId}/end-all`,
      })
      .then((res) => res.data);
  }
}

export const $$privateApi = new PrivateApi($$api.httpClient);
